// src/components/events/EventsList.jsx
import React, { useState } from 'react';
import EventDetail from './EventDetail';
import Badge from '../common/Badge';
import { eventSourceLabel } from '../../utils/eventSource';
import EnforcementOffLabel from '../common/EnforcementOffLabel';
import { eventEnforcementOff } from '../../utils/monitorMode';
import { edgeNodeDisplayName } from '../../utils/edgeNodeDisplayName';

const EventsList = ({ events = [], edgeNodes = [] }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  // The site's display name: the edge-node row when the page has it, else the title-cased id.
  const siteName = (id) => {
    if (!id) return 'N/A';
    return edgeNodeDisplayName(edgeNodes.find((n) => n.edge_node_id === id) || { edge_node_id: id });
  };

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp + 'Z');
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

  // Get threat pattern count
  const getThreatPatternCount = (event) => {
    const patterns = event.details?.triggering_event_details?.detected_patterns || [];
    return patterns.length;
  };

  if (!events || events.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-600 text-lg">No security events found</p>
        <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Severity
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Edge Agent
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Destination
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Protocol
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Policy
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Threats
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {events.map((event) => {
              const threatCount = getThreatPatternCount(event);
              
              return (
                <tr 
                  key={event.event_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                >
                  {/* Timestamp */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatTimestamp(event.timestamp)}
                  </td>

                  {/* Severity */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge type={event.severity} />
                  </td>

                  {/* Edge Agent */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {siteName(event.celerway_edge_node_id)}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Source IP */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {eventSourceLabel(event)}
                  </td>

                  {/* Destination IP */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {event.destination_ip || 'N/A'}
                  </td>

                  {/* Protocol */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {event.protocol || 'N/A'}
                    </span>
                  </td>

                  {/* Policy */}
                  <td className="px-6 py-4 max-w-xs">
                    <div className="text-sm text-gray-900 truncate" title={event.details?.policy_name}>
                      {event.details?.policy_name || '—'}
                    </div>
                    {event.details?.rule_name && (
                      <div className="text-xs text-gray-500 truncate" title={event.details?.rule_name}>
                        {event.details?.rule_name}
                      </div>
                    )}
                    {eventEnforcementOff(event) && (
                      <div className="mt-1"><EnforcementOffLabel /></div>
                    )}
                  </td>

                  {/* Threats */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {threatCount > 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {threatCount} detected
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">None</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      event.status === 'New' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {event.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEvent(event);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetail 
          event={selectedEvent} 
          onClose={() => setSelectedEvent(null)} 
          siteName={siteName(selectedEvent.celerway_edge_node_id)}
        />
      )}
    </>
  );
};

export default EventsList;
